import { inView } from 'motion';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';
import { isInView } from '@theme/utilities';

customElements.define(
  'sticky-buy-button',
  class StickyBuyButton extends HTMLElement {
    connectedCallback() {
      // Move to body to escape stacking context
      if (this.parentElement !== document.body) {
        document.body.appendChild(this);
        return; // connectedCallback will be called again after moving
      }

      this.addEventListener('click', (e) => {
        const target = /** @type {HTMLElement | null} */ (e.target);
        const anchor = target?.closest('a[href^="#"]');

        if (anchor) {
          e.preventDefault();
          const href = anchor.getAttribute('href');

          if (href) {
            document.querySelector(href)?.scrollIntoView({
              behavior: 'smooth',
            });
          }
        }
      });

      // Listen for variant updates
      document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);

      const targetId = this.dataset.targetId;
      const targetElement = targetId ? document.getElementById(targetId) : null;

      if (targetElement) {
        const footerElements = [...document.querySelectorAll('.shopify-section-group-footer-group')];
        const footersInView = new Set(footerElements.filter((el) => isInView(el)));

        // Show only once the main buy button has scrolled off the top, and no footer is in view.
        const update = () => {
          const isPastTarget = targetElement.getBoundingClientRect().bottom < 0;
          this.toggleAttribute('data-visible', isPastTarget && footersInView.size === 0);
        };

        update();

        inView(
          targetElement,
          () => {
            update();
            return update;
          },
          { amount: 'some' },
        );
        inView(
          footerElements,
          (el) => {
            footersInView.add(el);
            update();

            return () => {
              footersInView.delete(el);
              update();
            };
          },
          { amount: 'some' },
        );
      }
    }

    disconnectedCallback() {
      document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    }

    /**
     * Handles variant update events to sync with new variant
     * @param {import('@theme/events').VariantUpdateEvent} event
     */
    #onVariantUpdate = (event) => {
      const { html } = event.detail.data;

      // Match by target id so a foreign sticky button (another product context in the fetched page)
      // isn't morphed in. Fall back to the first one when no target id is set.
      const targetId = this.dataset.targetId;
      const newStickyButton =
        (targetId && html.querySelector(`sticky-buy-button[data-target-id="${CSS.escape(targetId)}"]`)) ||
        html.querySelector('sticky-buy-button');
      if (newStickyButton) {
        morph(this, newStickyButton);
      }
    };
  },
);
